/* @flow */
import * as React from 'react';
import AvatarEditorModal from 'components/AvatarEditorModal.jsx';
import WebcamAvatarModal from 'components/WebcamAvatarModal.jsx';
import ModalTrigger from 'components/ModalTrigger.jsx';
import MenuButton from 'components/MenuButton.jsx';
import MenuItem from 'components/MenuItem.jsx';
import Button from 'components/Button.jsx';
import Webcam from 'components/Webcam.jsx';
import Glyph from 'components/Glyph.jsx';
import { mountModal } from 'lib/modal';
import Menu from 'components/Menu.jsx';
import FileReader from 'components/FileReader.jsx';
import type { FileReaderFile } from 'components/FileReader.jsx';

/**
 * ## Button triggers browser file explorer and passes the file to AvatarEditorModal
 *
 * When the button is clicked, it triggers file explorer from <input>. When a
 * file is selected, the onChange handler for <input> fires and loads the
 * file as a dataUrl string. This dataUrl is passed to the AvatarEditorModal
 * which can render it as an image.
 *
 * ```example
 * <AvatarEditorModalTriggerButton>
 *  Click here to upload file
 * </AvatarEditorModalTriggerButton>
 * ```
 */

type Props = {
  onSuccess: Function,
  children?: React.Node,
  fileContextKey: string,
  uploadRequest: Function,
  loadThumbnail: Function,
};

export default class AvatarEditorModalTriggerButton extends React.Component<Props> {
  fileReader: *;

  handleFileRead = (file: FileReaderFile) => {
    mountModal(
      <AvatarEditorModal
        image={file.dataURL}
        filename={file.name}
        onSuccess={this.props.onSuccess}
        loadThumbnail={this.props.loadThumbnail}
        uploadRequest={this.props.uploadRequest}
        fileContextKey={this.props.fileContextKey}
      />,
      this
    );
  };

  openFileExplorer = () => {
    if (this.fileReader) {
      this.fileReader.openExplorer();
    }
  };

  renderChoicesMenu() {
    const webcam = (
      <ModalTrigger
        modal={
          <WebcamAvatarModal
            onSuccess={this.props.onSuccess}
            uploadRequest={this.props.uploadRequest}
            loadThumbnail={this.props.loadThumbnail}
            fileContextKey={this.props.fileContextKey}
          />
        }>
        <MenuItem>
          <Glyph name="camera" size="medium" className="mrm" />
          Take picture from your webcam
        </MenuItem>
      </ModalTrigger>
    );

    const menu = (
      <Menu>
        {webcam}
        <MenuItem onClick={this.openFileExplorer}>
          <Glyph name="upload" size="medium" className="mrm" />
          Upload picture from your computer
        </MenuItem>
      </Menu>
    );

    return (
      <MenuButton level="icon" alignment="middle" menu={menu}>
        {this.props.children}
      </MenuButton>
    );
  }

  renderFilePicker() {
    const { children, ...rest } = this.props;

    return (
      <Button {...rest} onClick={this.openFileExplorer}>
        {children}
      </Button>
    );
  }

  render() {
    return (
      <div>
        <FileReader ref={p => (this.fileReader = p)} onRead={this.handleFileRead} />
        {Webcam.isSupported() ? this.renderChoicesMenu() : this.renderFilePicker()}
      </div>
    );
  }
}
